import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PictureAsPdf as PdfIcon,
  Edit as EditIcon,
  Share as ShareIcon,
  WhatsApp as WhatsAppIcon,
  Telegram as TelegramIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { paymentsApi, openRazorpayCheckout } from '../../services/paymentsApi';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined' && (window.location.hostname.includes('ondigitalocean.app') || window.location.hostname.includes('services.knls.in'))) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();
const DEFAULT_PDF_FEE_RUPEES = 20;
const DEFAULT_DOCX_FEE_RUPEES = 0;
const DEFAULT_PDF_MINIMALIST_FEE_RUPEES = 25;
const DEFAULT_PDF_MODERN_FEE_RUPEES = 30;
const DEFAULT_PDF_CREATIVE_FEE_RUPEES = 30;

const TEMPLATE_OPTIONS = [
  { id: 'classic', label: 'Classic' },
  { id: 'minimalist', label: 'Minimalist' },
  { id: 'modern', label: 'Modern' },
  { id: 'creative', label: 'Creative' },
];

const paidProfileHashKey = (userId) => `resume_paid_profile_hash_${userId}`;

const getProfileHash = (profile) => {
  if (!profile || typeof profile !== 'object') return '';
  const keyFields = {
    fullName: profile.fullName || '',
    email: profile.email || '',
    phone: profile.phone || profile.mobile || '',
    city: profile.city || '',
    state: profile.state || '',
    pincode: profile.pincode || '',
    address: profile.address || '',
    qualification: profile.qualification || '',
    skills: profile.skills || '',
    objective: profile.objective || '',
    experienceType: profile.experienceType || '',
    designation: profile.designation || '',
    company: profile.company || '',
    sscPercentage: profile.sscPercentage || '',
    sscSchool: profile.sscSchool || '',
    sscYear: profile.sscYear || '',
    interPercentage: profile.interPercentage || '',
    interSchool: profile.interSchool || '',
    interYear: profile.interYear || '',
    degreeName: profile.degreeName || '',
    degreePercentage: profile.degreePercentage || '',
    degreeCollege: profile.degreeCollege || '',
    degreeYear: profile.degreeYear || '',
    pgName: profile.pgName || '',
    pgPercentage: profile.pgPercentage || '',
    pgCollege: profile.pgCollege || '',
    pgYear: profile.pgYear || '',
  };
  return JSON.stringify(keyFields);
};

const ResumeGeneration = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [profile, setProfile] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [page1Html, setPage1Html] = useState(null);
  const [page2Html, setPage2Html] = useState(null);
  const [generatingDocx, setGeneratingDocx] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('classic');
  const [templates, setTemplates] = useState([]);
  const [pdfFeeRupees, setPdfFeeRupees] = useState(DEFAULT_PDF_FEE_RUPEES);
  const [pdfMinimalistFeeRupees, setPdfMinimalistFeeRupees] = useState(
    DEFAULT_PDF_MINIMALIST_FEE_RUPEES,
  );
  const [pdfModernFeeRupees, setPdfModernFeeRupees] = useState(DEFAULT_PDF_MODERN_FEE_RUPEES);
  const [pdfCreativeFeeRupees, setPdfCreativeFeeRupees] = useState(DEFAULT_PDF_CREATIVE_FEE_RUPEES);
  const [docxFeeRupees, setDocxFeeRupees] = useState(DEFAULT_DOCX_FEE_RUPEES);
  const [editFeeRupees, setEditFeeRupees] = useState(0);

  useEffect(() => {
    const loadResumePricing = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/career-profiles/settings`);
        if (res.data?.success && res.data.settings) {
          const s = res.data.settings;
          const pdf = Number(s.pdf_fee_rupees);
          const pdfMinimalist = Number(
            s.pdf_minimalist_fee_rupees ?? s.pdf_compact_fee_rupees,
          );
          const pdfModern = Number(s.pdf_modern_fee_rupees);
          const pdfCreative = Number(
            s.pdf_creative_fee_rupees ?? s.pdf_executive_fee_rupees,
          );
          const docx = Number(s.docx_fee_rupees);
          const edit = Number(s.edit_fee_rupees);
          if (Number.isFinite(pdf) && pdf >= 0) setPdfFeeRupees(pdf);
          if (Number.isFinite(pdfMinimalist) && pdfMinimalist >= 0) {
            setPdfMinimalistFeeRupees(pdfMinimalist);
          }
          if (Number.isFinite(pdfModern) && pdfModern >= 0) setPdfModernFeeRupees(pdfModern);
          if (Number.isFinite(pdfCreative) && pdfCreative >= 0) {
            setPdfCreativeFeeRupees(pdfCreative);
          }
          if (Number.isFinite(docx) && docx >= 0) setDocxFeeRupees(docx);
          if (Number.isFinite(edit) && edit >= 0) setEditFeeRupees(edit);
          if (Array.isArray(s.templates)) setTemplates(s.templates);
        }
      } catch (err) {
        console.warn('Could not load resume pricing settings, using defaults');
      }
    };
    loadResumePricing();
  }, []);

  const getPdfModelFee = (templateId) => {
    switch (templateId) {
      case 'minimalist':
      case 'compact':
        return pdfMinimalistFeeRupees;
      case 'modern':
        return pdfModernFeeRupees;
      case 'creative':
      case 'executive':
        return pdfCreativeFeeRupees;
      case 'classic':
      default:
        return pdfFeeRupees;
    }
  };

  const profileNeedsEditFee = (userId, currentProfile) => {
    if (!userId || !currentProfile || !(editFeeRupees > 0)) return false;
    const paidHash = localStorage.getItem(paidProfileHashKey(userId));
    if (!paidHash) return false; // 1st build = model fee only
    return paidHash !== getProfileHash(currentProfile);
  };

  const savePaidProfileHash = (userId, currentProfile) => {
    if (!userId || !currentProfile) return;
    localStorage.setItem(paidProfileHashKey(userId), getProfileHash(currentProfile));
  };

  const userIdForFees = user?.id || user?.userId;
  const applyEditFee = profileNeedsEditFee(userIdForFees, profile);
  const pdfTotalFeeRupees = getPdfModelFee(selectedTemplate) + (applyEditFee ? editFeeRupees : 0);
  const docxTotalFeeRupees = docxFeeRupees + (applyEditFee ? editFeeRupees : 0);

  const templateChoices = (() => {
    if (Array.isArray(templates) && templates.length > 0) {
      return templates
        .map((t) => {
          if (typeof t === 'string') {
            const opt = TEMPLATE_OPTIONS.find((o) => o.id === t);
            return opt || { id: t, label: t.charAt(0).toUpperCase() + t.slice(1) };
          }
          if (t && t.id) {
            return { id: t.id, label: t.label || t.name || t.id };
          }
          return null;
        })
        .filter(Boolean);
    }
    return TEMPLATE_OPTIONS;
  })();

  const handlePhotoSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setProfile((prev) => (prev ? { ...prev, photoBase64: reader.result } : prev));
    };
    reader.onerror = () => toast.error('Failed to read photo');
    reader.readAsDataURL(file);
  };

  const injectResumePhoto = (html, photoBase64, template) => {
    if ((template || 'classic') === 'classic' || !photoBase64) return html;
    const photoHtml = `<div class="resume-photo-wrap"><img src="${photoBase64}" style="width:72px;height:72px;object-fit:cover;float:right;margin:0 0 8px 10px;border-radius:8px;border:2px solid #0F766E" /></div>`;
    if (html.includes('<div class="resume-header">')) {
      return html.replace('<div class="resume-header">', `<div class="resume-header">${photoHtml}`);
    }
    const pageOpen = html.match(/<div class="page"[^>]*>/);
    if (pageOpen) {
      return html.replace(pageOpen[0], `${pageOpen[0]}${photoHtml}`);
    }
    return html;
  };

  /**
   * STRICT SEPARATION OF CONCERNS:
   * Frontend PREVIEW responsibility:
   * 1. Fetch rendered HTML from backend (pure HTML, NO React)
   * 2. Display inside iframe (NO React DOM manipulation)
   * 3. Scroll enabled for preview
   * 
   * CRITICAL:
   * - Frontend does NOT generate HTML
   * - Frontend does NOT influence PDF generation
   * - Frontend only DISPLAYS backend-generated HTML
   * - PDF generation happens entirely in backend (PDFKit - scalable, no browser needed)
   */
  const loadPreview = async () => {
    const userId = user?.id || user?.userId;
    if (!userId) return;

    const token = localStorage.getItem('user_token');
    if (!token) {
      console.error('No token found for preview');
      return;
    }

    setPreviewLoading(true);
    try {
      // Fetch pure HTML from backend (static template + string replacement)
      // NO React rendering, NO JSX, pure HTML string only
      const templateId = selectedTemplate || 'classic';
      const response = await axios.get(
        `${API_BASE_URL}/career-profiles/${userId}/preview?template=${encodeURIComponent(templateId)}`,
        {
          headers: {
                    Authorization: `Bearer ${localStorage.getItem('user_token')}`,
          },
          responseType: 'text', // Get HTML as text (NOT React components)
        }
      );
      let fullHtml = response.data; // Pure HTML string from backend
      if (templateId !== 'classic' && profile?.photoBase64) {
        fullHtml = injectResumePhoto(fullHtml, profile.photoBase64, templateId);
      }
      setPreviewHtml(fullHtml);
      
      // Split HTML into Page 1 and Page 2
      // Extract head and body opening tag
      const headMatch = fullHtml.match(/<head>[\s\S]*?<\/head>/);
      const bodyMatch = fullHtml.match(/<body[^>]*>/);
      
      const htmlHead = headMatch ? headMatch[0] : '<head><meta charset="UTF-8"><title>Resume</title></head>';
      const bodyStart = bodyMatch ? bodyMatch[0] : '<body>';
      
      // Find all page divs - need to match nested divs properly
      const pages = [];
      let searchStart = 0;
      
      // Find all occurrences of <div class="page"
      while (true) {
        const pageStart = fullHtml.indexOf('<div', searchStart);
        if (pageStart === -1) break;
        
        // Check if this div has class="page"
        const divTagEnd = fullHtml.indexOf('>', pageStart);
        if (divTagEnd === -1) break;
        
        const divTag = fullHtml.substring(pageStart, divTagEnd + 1);
        if (divTag.includes('class="page"') || divTag.includes("class='page'")) {
          // Found a page div start - now find its matching closing tag
          let depth = 1;
          let pos = divTagEnd + 1;
          
          while (depth > 0 && pos < fullHtml.length) {
            const nextOpen = fullHtml.indexOf('<div', pos);
            const nextClose = fullHtml.indexOf('</div>', pos);
            
            if (nextClose === -1) break; // No more closing tags
            
            if (nextOpen !== -1 && nextOpen < nextClose) {
              // Found another opening div before closing
              depth++;
              pos = nextOpen + 4;
            } else {
              // Found a closing div
              depth--;
              if (depth === 0) {
                // Found the matching closing tag
                const pageEnd = nextClose + 6; // Include </div>
                const pageContent = fullHtml.substring(pageStart, pageEnd);
                pages.push(pageContent);
                searchStart = pageEnd;
                break;
              }
              pos = nextClose + 6;
            }
          }
        } else {
          searchStart = divTagEnd + 1;
        }
      }
      
      // Add preview-specific CSS - keep A4 size but adjust padding
      const previewStyle = `
        <style>
          /* Keep A4 size for preview so user can see page layout */
          .page {
            min-height: 297mm !important;
            height: auto !important;
          }
          body {
            padding: 20px !important;
          }
        </style>
      `;
      
      // Inject preview style into head
      const headWithPreviewStyle = htmlHead.replace('</head>', `${previewStyle}</head>`);
      
      if (pages.length >= 2) {
        // Page 1 HTML - only first page div
        const page1Html = `<!DOCTYPE html><html lang="en">${headWithPreviewStyle}${bodyStart}${pages[0]}</body></html>`;
        
        // Page 2 HTML - only second page div
        const page2Html = `<!DOCTYPE html><html lang="en">${headWithPreviewStyle}${bodyStart}${pages[1]}</body></html>`;
        
        setPage1Html(page1Html);
        setPage2Html(page2Html);
      } else if (pages.length === 1) {
        // Only one page found - use it for page 1, show empty for page 2
        const page1Html = `<!DOCTYPE html><html lang="en">${headWithPreviewStyle}${bodyStart}${pages[0]}</body></html>`;
        const page2Html = `<!DOCTYPE html><html lang="en">${headWithPreviewStyle}${bodyStart}<div class="page"><p style="padding: 20px;">Page 2 content will appear here</p></div></body></html>`;
        
        setPage1Html(page1Html);
        setPage2Html(page2Html);
      } else {
        // Fallback: if no page divs found, use full HTML for page 1, empty for page 2
        setPage1Html(fullHtml.replace('</head>', `${previewStyle}</head>`));
        setPage2Html(`<!DOCTYPE html><html lang="en">${headWithPreviewStyle}${bodyStart}<div class="page"><p style="padding: 20px;">Page 2 content will appear here</p></div></body></html>`);
      }
    } catch (error) {
      console.error('Failed to load preview:', error);
      const errorHtml = `<html><body><p style="padding: 20px; color: red;">Error loading preview: ${error.response?.data?.message || error.message}</p></body></html>`;
      setPreviewHtml(errorHtml);
      setPage1Html(errorHtml);
      setPage2Html(errorHtml);
    } finally {
      setPreviewLoading(false);
    }
  };

  const loadProfile = async () => {
    if (user?.id || user?.userId) {
      const userId = user.id || user.userId;
      if (!userId) return;
      
      setLoading(true);
      try {
        const token = localStorage.getItem('user_token');
        const response = await axios.get(`${API_BASE_URL}/career-profiles/${userId}`, {
          headers: {
                    Authorization: `Bearer ${localStorage.getItem('user_token')}`,
          },
          timeout: 10000, // 10 second timeout
        });
        
        if (response.data.success && response.data.data) {
          let data = response.data.data.profile_json || response.data.data;
          
          // Parse profile_json if it's a string
          if (typeof data === 'string') {
            try {
              data = JSON.parse(data);
            } catch (parseError) {
              console.error('Error parsing profile_json:', parseError);
              data = response.data.data;
            }
          }
          
          // Merge direct fields from response.data.data if they exist
          if (data && typeof data === 'object' && !Array.isArray(data)) {
            const directFields = response.data.data;
            if (directFields.full_name && !data.fullName) data.fullName = directFields.full_name;
            if (directFields.phone && !data.phone) data.phone = directFields.phone;
            if (directFields.email && !data.email) data.email = directFields.email;
            if (directFields.city && !data.city) data.city = directFields.city;
            if (directFields.state && !data.state) data.state = directFields.state;
            if (directFields.pincode && !data.pincode) data.pincode = directFields.pincode;
            if (directFields.address && !data.address) data.address = directFields.address;
            if (directFields.qualification && !data.qualification) data.qualification = directFields.qualification;
            if (directFields.skills && !data.skills) data.skills = directFields.skills;
            if (directFields.objective && !data.objective) data.objective = directFields.objective;
            if (directFields.experience_type && !data.experienceType) data.experienceType = directFields.experience_type;
            if (directFields.designation && !data.designation) data.designation = directFields.designation;
            if (directFields.company && !data.company) data.company = directFields.company;
            // Add education fields
            if (directFields.ssc_percentage && !data.sscPercentage) data.sscPercentage = directFields.ssc_percentage;
            if (directFields.ssc_school && !data.sscSchool) data.sscSchool = directFields.ssc_school;
            if (directFields.ssc_year && !data.sscYear) data.sscYear = directFields.ssc_year;
            if (directFields.inter_percentage && !data.interPercentage) data.interPercentage = directFields.inter_percentage;
            if (directFields.inter_school && !data.interSchool) data.interSchool = directFields.inter_school;
            if (directFields.inter_year && !data.interYear) data.interYear = directFields.inter_year;
            if (directFields.degree_name && !data.degreeName) data.degreeName = directFields.degree_name;
            if (directFields.degree_percentage && !data.degreePercentage) data.degreePercentage = directFields.degree_percentage;
            if (directFields.degree_college && !data.degreeCollege) data.degreeCollege = directFields.degree_college;
            if (directFields.degree_year && !data.degreeYear) data.degreeYear = directFields.degree_year;
            if (directFields.pg_name && !data.pgName) data.pgName = directFields.pg_name;
            if (directFields.pg_percentage && !data.pgPercentage) data.pgPercentage = directFields.pg_percentage;
            if (directFields.pg_college && !data.pgCollege) data.pgCollege = directFields.pg_college;
            if (directFields.pg_year && !data.pgYear) data.pgYear = directFields.pg_year;
          }
          
          setProfile(data);
          toast.success('Profile refreshed successfully!');
        } else {
          toast.info('Please complete your career profile first');
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        if (error.code === 'ECONNABORTED') {
          toast.error('Request timeout. Please check your connection.');
        } else {
          toast.error('Failed to load profile. Please complete your career profile first.');
        }
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (user) {
      loadProfile();
    }
  }, [user]);

  useEffect(() => {
    if (user && profile) {
      loadPreview();
    }
  }, [user, profile, selectedTemplate, profile?.photoBase64]);

  const openPreviewInNewTab = async () => {
    const userId = user?.id || user?.userId;
    if (!userId) {
      toast.error('User not authenticated');
      return;
    }

    if (!profile) {
      toast.error('Please complete your career profile first');
      navigate('/user/career-profile');
      return;
    }

    const token = localStorage.getItem('user_token');
    if (!token) {
      toast.error('Authentication token not found. Please log in again.');
      return;
    }

    try {
      // Fetch HTML from backend preview endpoint
      const templateId = selectedTemplate || 'classic';
      const response = await axios.get(
        `${API_BASE_URL}/career-profiles/${userId}/preview?template=${encodeURIComponent(templateId)}`,
        {
          headers: {
                    Authorization: `Bearer ${localStorage.getItem('user_token')}`,
          },
          responseType: 'text', // Get HTML as text
        }
      );
      
      let html = response.data;
      if (templateId !== 'classic' && profile?.photoBase64) {
        html = injectResumePhoto(html, profile.photoBase64, templateId);
      }
      
      // Create a blob URL from the HTML
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      
      // Open in new tab
      const newWindow = window.open(url, '_blank');
      
      if (!newWindow) {
        toast.error('Please allow popups to view HTML preview');
        URL.revokeObjectURL(url);
        return;
      }
      
      // Clean up blob URL after a delay (window should have loaded it)
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);
      
      toast.success('HTML preview opened in new tab');
    } catch (error) {
      console.error('Error opening HTML preview:', error);
      toast.error('Failed to load HTML preview. Please try again.');
    }
  };

  const downloadPaidResumePdf = async () => {
    const userId = user?.id || user?.userId;
    if (!userId) {
      toast.error('User not authenticated');
      return;
    }

    if (!profile) {
      toast.error('Please complete your career profile first');
      navigate('/user/career-profile');
      return;
    }

    setGenerating(true);

    const token = localStorage.getItem('user_token');
    if (!token) {
      toast.error('Authentication token not found. Please log in again.');
      setGenerating(false);
      return;
    }

    try {
      // Use fetch API instead of axios for better blob and header handling
      // Fetch API preserves headers better than axios for blob responses
      let response;
      try {
        response = await fetch(
          `${API_BASE_URL}/career-profiles/${userId}/generate-resume`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('user_token')}`,
            },
            body: JSON.stringify({
              format: 'pdf',
              template: selectedTemplate || 'classic',
              ...(profile?.photoBase64 && selectedTemplate !== 'classic'
                ? { photoBase64: profile.photoBase64 }
                : {}),
            }),

            credentials: 'include', // Include cookies for CORS
          }
        );
      } catch (fetchError) {
        console.error('Fetch error:', fetchError);
        // If fetch fails, it might be a CORS or network issue
        // Fall back to axios for compatibility
        throw new Error(`Network error: ${fetchError.message}. Please check your connection and try again.`);
      }

      // CRITICAL: Check if response is actually a PDF before processing
      const contentType = response.headers.get('content-type') || '';
      
      // Check if response is OK
      if (!response.ok) {
        // Try to parse error as JSON (clone response first to avoid consuming it)
        const errorText = await response.clone().text();
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.message || errorData.error || 'Failed to generate resume');
        } catch (parseError) {
          throw new Error(`Server error: ${response.status} ${response.statusText}`);
        }
      }
      
      // Check if it's a JSON error response (even if status is OK)
      if (contentType.includes('application/json') || contentType.includes('text/html') || contentType.includes('text/plain')) {
        const errorText = await response.clone().text();
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.message || errorData.error || 'Failed to generate resume');
        } catch (parseError) {
          throw new Error('Server returned an error response. Please try again.');
        }
      }
      
      // Validate it's a PDF
      if (!contentType.includes('application/pdf')) {
        throw new Error(`Unexpected content type: ${contentType}. Expected application/pdf.`);
      }
      
      // GOVERNMENT SITES APPROACH: Get array buffer first, then create blob with explicit type
      const arrayBuffer = await response.arrayBuffer();
      
      // Validate PDF header
      const uint8Array = new Uint8Array(arrayBuffer);
      const header = String.fromCharCode(...uint8Array.slice(0, 4));
      
      if (header !== '%PDF') {
        console.error('Invalid PDF header:', header);
        throw new Error('Downloaded file is not a valid PDF. Please try again.');
      }
      
      // Extract filename from headers (government sites approach)
      const fullName = (profile.fullName || 'Resume').trim();
      const firstName = fullName.split(/\s+/)[0].toLowerCase().replace(/[^a-zA-Z0-9]/g, '');
      let fileName = `${firstName}_resume.pdf`;
      
      // Try X-Filename header first (most reliable)
      const customFilename = response.headers.get('x-filename');
      if (customFilename) {
        fileName = customFilename;
      } else {
        // Fallback: Parse Content-Disposition header
        const contentDisposition = response.headers.get('content-disposition');
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            fileName = filenameMatch[1].replace(/['"]/g, '');
          }
        }
      }
      
      // Clean filename
      fileName = fileName.replace(/[<>:"/\\|?*]/g, '_').replace(/\s+/g, '_');
      if (!fileName.toLowerCase().endsWith('.pdf')) {
        fileName = fileName.replace(/\.[^.]*$/, '') + '.pdf';
      }
      
      console.log('📥 Downloading PDF:', {
        fileName,
        size: arrayBuffer.byteLength,
        header: header
      });

      // GOVERNMENT SITES APPROACH: Create blob with explicit MIME type
      // This ensures browser recognizes it as PDF
      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      
      // Create blob URL
      const url = window.URL.createObjectURL(blob);
      
      // Create download link (government sites simple approach)
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName; // CRITICAL: Set download attribute with filename
      link.style.display = 'none'; // Hide link
      
      document.body.appendChild(link);
      
      // Trigger download immediately
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      console.log('✅ PDF download initiated:', fileName);
      
      // Store blob for sharing
      setPdfBlob(blob);
      setShowShareOptions(true);

      // Remember profile hash after successful PDF so later edits can add edit fee
      savePaidProfileHash(userId, profile);
      
      // Show success message
      toast.success(`Resume downloaded successfully as ${fileName}`);
    } catch (error) {
      console.error('Resume generation error:', error);
      
      // Handle blob response that might be JSON error
      if (error.response?.data instanceof Blob) {
        try {
          const text = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsText(error.response.data);
          });
          
          const errorData = JSON.parse(text);
          if (errorData.message) {
            toast.error(errorData.message);
          setGenerating(false);
          return;
          }
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
        }
      }
      
      // Extract error message
      let errorMessage = 'Failed to generate resume. Please try again.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  const generateResume = async () => {
    const userId = user?.id || user?.userId;
    if (!userId) {
      toast.error('User not authenticated');
      return;
    }

    if (!profile) {
      toast.error('Please complete your career profile first');
      navigate('/user/career-profile');
      return;
    }

    const token = localStorage.getItem('user_token');
    if (!token) {
      toast.error('Authentication token not found. Please log in again.');
      return;
    }

    setGenerating(true);
    try {
      if (pdfTotalFeeRupees <= 0) {
        toast.info('Generating PDF...');
        await downloadPaidResumePdf();
        return;
      }

      const orderRes = await paymentsApi.createRazorpayOrder({
        amount: Math.round(pdfTotalFeeRupees * 100),
        currency: 'INR',
        receipt: `resume_pdf_${String(userId).replace(/[^a-zA-Z0-9_]/g, '_')}`.slice(0, 39),
        billId: null,
      });

      if (!orderRes?.success || !orderRes.order || !orderRes.keyId) {
        toast.error(orderRes?.message || 'Razorpay is not available. Check server configuration.');
        setGenerating(false);
        return;
      }

      let paymentSucceeded = false;
      await openRazorpayCheckout({
        order: orderRes.order,
        keyId: orderRes.keyId,
        customer: {
          name: profile.fullName || user?.name || user?.fullName || '',
          phone: profile.mobile || profile.phone || user?.phone || '',
        },
        description: `Resume PDF (${selectedTemplate}) - ₹${pdfTotalFeeRupees}`,
        onDismiss: () => {
          if (!paymentSucceeded) {
            setGenerating(false);
            toast.info('Payment was cancelled. PDF was not generated.');
          }
        },
        onSuccess: async (rzResponse) => {
          paymentSucceeded = true;
          try {
            const verifyRes = await paymentsApi.verifyRazorpayPayment({
              razorpay_order_id: rzResponse.razorpay_order_id,
              razorpay_payment_id: rzResponse.razorpay_payment_id,
              razorpay_signature: rzResponse.razorpay_signature,
            });

            if (!verifyRes?.success && !verifyRes?.verified) {
              toast.error('Payment could not be verified. PDF was not generated.');
              setGenerating(false);
              return;
            }

            toast.success('Payment successful. Generating PDF...');
            await downloadPaidResumePdf();
          } catch (error) {
            console.error('Resume PDF payment verification error:', error);
            toast.error(error?.message || 'Payment verification failed. PDF was not generated.');
            setGenerating(false);
          }
        },
      });
    } catch (error) {
      console.error('Resume PDF payment error:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Could not start payment.');
      setGenerating(false);
    }
  };

  const downloadDocxResume = async () => {
    const userId = user?.id || user?.userId;
    const token = localStorage.getItem('user_token');
    if (!token) {
      toast.error('Authentication token not found. Please log in again.');
      setGeneratingDocx(false);
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/career-profiles/${userId}/generate-docx`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('user_token')}`,
          },
          credentials: 'include',
          body: JSON.stringify({
            template: selectedTemplate || 'classic',
            ...(profile?.photoBase64 && selectedTemplate !== 'classic'
              ? { photoBase64: profile.photoBase64 }
              : {}),
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.clone().text();
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.message || errorData.error || 'Failed to generate DOCX resume');
        } catch (parseError) {
          throw new Error(`Server error: ${response.status} ${response.statusText}`);
        }
      }

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/vnd.openxmlformats-officedocument.wordprocessingml.document')) {
        const errorText = await response.text();
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.message || 'Failed to generate DOCX resume');
        } catch (parseError) {
          throw new Error('Unexpected content type. Expected DOCX file.');
        }
      }

      const blob = await response.blob();

      // Extract first word from name as fallback
      const fullName = (profile.fullName || 'Resume').trim();
      const firstName = fullName.split(/\s+/)[0].toLowerCase().replace(/[^a-zA-Z0-9]/g, '');
      let fileName = `${firstName}_resume.docx`; // Fallback filename
      
      // Read filename from custom header (RELIABLE)
      const customFilename = response.headers.get('x-filename');
      if (customFilename) {
        fileName = customFilename;
        console.log('Extracted filename from X-Filename header:', fileName);
      } else {
        console.log('X-Filename header not found, using fallback:', fileName);
      }
      
      // Clean filename: remove special characters that might cause issues
      fileName = fileName.replace(/[<>:"/\\|?*]/g, '_');
      
      // Ensure filename ends with .docx
      if (!fileName.toLowerCase().endsWith('.docx')) {
        fileName = fileName.replace(/\.[^.]*$/, '') + '.docx';
      }

      // Create blob URL directly from blob
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = fileName; // Set download attribute BEFORE href for Chrome
      link.href = url;
      link.setAttribute('download', fileName);
      
      // For Firefox compatibility: ensure link is in DOM and visible (but off-screen)
      link.style.position = 'fixed';
      link.style.left = '-9999px';
      link.style.top = '-9999px';
      link.style.opacity = '0';
      link.style.pointerEvents = 'none';
      link.style.display = 'block';
      
      document.body.appendChild(link);
      
      // Force a reflow to ensure the link is ready
      void link.offsetHeight;
      
      // Use direct click() for Firefox compatibility
      setTimeout(() => {
        try {
          link.click();
          console.log('DOCX download triggered for:', fileName);
        } catch (e) {
          console.error('Error triggering download:', e);
          try {
            const clickEvent = new MouseEvent('click', {
              view: window,
              bubbles: true,
              cancelable: true,
              buttons: 1
            });
            link.dispatchEvent(clickEvent);
          } catch (e2) {
            console.error('Fallback download method also failed:', e2);
          }
        }
      }, 100);
      
      // Clean up: remove link and revoke blob URL after download completes
      setTimeout(() => {
        if (document.body.contains(link)) {
          document.body.removeChild(link);
        }
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          console.log('Blob URL revoked');
        }, 3000);
      }, 500);
      
      savePaidProfileHash(userId, profile);
      toast.success(`DOCX resume generated successfully! Downloading as ${fileName}`);
    } catch (error) {
      console.error('DOCX generation error:', error);
      
      let errorMessage = 'Failed to generate DOCX resume. Please try again.';
      if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    } finally {
      setGeneratingDocx(false);
    }
  };

  const generateDocx = async () => {
    const userId = user?.id || user?.userId;
    if (!userId) {
      toast.error('User not authenticated');
      return;
    }

    if (!profile) {
      toast.error('Please complete your career profile first');
      navigate('/user/career-profile');
      return;
    }

    setGeneratingDocx(true);

    if (docxTotalFeeRupees <= 0) {
      await downloadDocxResume();
      return;
    }

    try {
      const orderRes = await paymentsApi.createRazorpayOrder({
        amount: Math.round(docxTotalFeeRupees * 100),
        currency: 'INR',
        receipt: `resume_docx_${String(userId).replace(/[^a-zA-Z0-9_]/g, '_')}`.slice(0, 39),
        billId: null,
      });

      if (!orderRes?.success || !orderRes.order || !orderRes.keyId) {
        toast.error(orderRes?.message || 'Razorpay is not available. Check server configuration.');
        setGeneratingDocx(false);
        return;
      }

      let paymentSucceeded = false;
      await openRazorpayCheckout({
        order: orderRes.order,
        keyId: orderRes.keyId,
        customer: {
          name: profile.fullName || user?.name || user?.fullName || '',
          phone: profile.mobile || profile.phone || user?.phone || '',
        },
        description: `Resume DOCX generation - ₹${docxTotalFeeRupees}`,
        onDismiss: () => {
          if (!paymentSucceeded) {
            setGeneratingDocx(false);
            toast.info('Payment was cancelled. DOCX was not generated.');
          }
        },
        onSuccess: async (rzResponse) => {
          paymentSucceeded = true;
          try {
            const verifyRes = await paymentsApi.verifyRazorpayPayment({
              razorpay_order_id: rzResponse.razorpay_order_id,
              razorpay_payment_id: rzResponse.razorpay_payment_id,
              razorpay_signature: rzResponse.razorpay_signature,
            });

            if (!verifyRes?.success && !verifyRes?.verified) {
              toast.error('Payment could not be verified. DOCX was not generated.');
              setGeneratingDocx(false);
              return;
            }

            toast.success('Payment successful. Generating DOCX...');
            await downloadDocxResume();
          } catch (error) {
            console.error('Resume DOCX payment verification error:', error);
            toast.error(error?.message || 'Payment verification failed. DOCX was not generated.');
            setGeneratingDocx(false);
          }
        },
      });
    } catch (error) {
      console.error('Resume DOCX payment error:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Could not start payment.');
      setGeneratingDocx(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box mb={3}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/user/dashboard')}
              sx={{ mb: 2 }}
            >
              Back to Dashboard
            </Button>
            <Typography variant="h4" gutterBottom>
              Resume Generation
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Generate professional resumes in PDF format
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadProfile}
            disabled={loading}
            sx={{ mt: 1 }}
          >
            Refresh Preview
          </Button>
        </Box>
      </Box>

      {!profile ? (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Profile Not Found
          </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Please complete your career profile first to generate a resume.
            </Typography>
              <Button
              variant="contained"
              startIcon={<EditIcon />}
                onClick={() => navigate('/user/career-profile')}
              >
                Complete Profile
              </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {/* Resume Preview Page - 1 */}
          <Grid item xs={12} md={6}>
            <Card elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Page Header */}
                <Box
                  sx={{
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #e0e0e0',
                    padding: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    Resume Preview Page - 1
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: '#1976d2',
                      color: 'white',
                      borderRadius: '50%',
                      width: 28,
                      height: 28,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.875rem',
                      fontWeight: 'bold',
                    }}
                  >
                    1
                  </Box>
                </Box>
                
                {/* Page Preview Container */}
                <Box
                  sx={{
                    width: '100%',
                    backgroundColor: '#f5f5f5',
                    padding: 1,
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: '380px',
                      height: '520px',
                      backgroundColor: 'white',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      position: 'relative',
                      border: '1px solid #ddd',
                      overflow: 'auto',
                    }}
                  >
                    {previewLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress />
                      </Box>
                    ) : page1Html ? (
                      <iframe
                        title="Resume Preview Page 1"
                        srcDoc={page1Html}
                        style={{
                          width: '100%',
                          height: '100%',
                          border: 'none',
                          display: 'block',
                          backgroundColor: 'white',
                        }}
                        scrolling="yes"
                      />
                    ) : (
                      <Box sx={{ padding: 2, textAlign: 'center', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          Loading Page 1...
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Resume Preview Page - 2 */}
          <Grid item xs={12} md={6}>
            <Card elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Page Header */}
                <Box
                  sx={{
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #e0e0e0',
                    padding: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    Resume Preview Page - 2
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: '#1976d2',
                      color: 'white',
                      borderRadius: '50%',
                      width: 28,
                      height: 28,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.875rem',
                      fontWeight: 'bold',
                    }}
                  >
                    2
                  </Box>
                </Box>
                
                {/* Page Preview Container - SMALL FIXED SIZE */}
                <Box
                  sx={{
                    width: '100%',
                    backgroundColor: '#f5f5f5',
                    padding: 1,
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: '380px',
                      height: '520px',
                      backgroundColor: 'white',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      position: 'relative',
                      border: '1px solid #ddd',
                      overflow: 'auto',
                    }}
                  >
                    {previewLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress />
                      </Box>
                    ) : page2Html ? (
                      <iframe
                        title="Resume Preview Page 2"
                        srcDoc={page2Html}
                        style={{
                          width: '100%',
                          height: '100%',
                          border: 'none',
                          display: 'block',
                          backgroundColor: 'white',
                        }}
                        scrolling="yes"
                      />
                    ) : (
                      <Box sx={{ padding: 2, textAlign: 'center', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          Loading Page 2...
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Generate Resume Section */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Generate PDF Resume
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {pdfTotalFeeRupees > 0
                    ? `Pay ₹${pdfTotalFeeRupees} securely, then the PDF will generate and download automatically.`
                    : 'PDF generation is free. Download starts immediately.'}
                  {docxTotalFeeRupees > 0
                    ? ` DOCX is ₹${docxTotalFeeRupees}.`
                    : ' DOCX is free.'}
                  {applyEditFee
                    ? ` Includes ₹${editFeeRupees} edit fee (profile changed since last paid download).`
                    : ''}
                </Typography>

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Template
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {templateChoices.map((opt) => (
                    <Button
                      key={opt.id}
                      variant={selectedTemplate === opt.id ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => setSelectedTemplate(opt.id)}
                      sx={{ textTransform: 'none', minWidth: 96 }}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </Box>

                {selectedTemplate !== 'classic' && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Optional photo (stored locally for this session)
                    </Typography>
                    <Button variant="outlined" component="label" size="small">
                      {profile?.photoBase64 ? 'Change photo' : 'Add photo'}
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handlePhotoSelect}
                      />
                    </Button>
                    {profile?.photoBase64 ? (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        Photo ready
                      </Typography>
                    ) : null}
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <PdfIcon />}
                    onClick={generateResume}
                    disabled={generating || generatingDocx || !profile}
                  >
                    {generating
                      ? 'Processing...'
                      : pdfTotalFeeRupees > 0
                        ? `Pay ₹${pdfTotalFeeRupees} & Generate PDF`
                        : 'Generate PDF'}
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    fullWidth
                    startIcon={generatingDocx ? <CircularProgress size={20} color="inherit" /> : <DescriptionIcon />}
                    onClick={generateDocx}
                    disabled={generating || generatingDocx || !profile}
                  >
                    {generatingDocx
                      ? 'Generating...'
                      : docxTotalFeeRupees > 0
                        ? `Pay ₹${docxTotalFeeRupees} & Generate .docx`
                        : 'Generate .docx'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="large"
                    startIcon={<VisibilityIcon />}
                    onClick={openPreviewInNewTab}
                    disabled={!profile}
                    sx={{ minWidth: 'auto' }}
                  >
                    View HTML
                  </Button>
                </Box>

                {/* Share Options */}
                {showShareOptions && pdfBlob && (
                  <Box sx={{ mt: 3 }}>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ShareIcon fontSize="small" />
                      Share Resume
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={4}>
                        <Button
                          variant="outlined"
                          fullWidth
                          size="small"
                          startIcon={<WhatsAppIcon />}
                          onClick={() => {
                            const fullName = (profile.fullName || 'Resume').trim();
                            const firstName = fullName.split(/\s+/)[0].toLowerCase().replace(/[^a-zA-Z0-9]/g, '');
                            const fileName = `${firstName}_resume.pdf`;
                            const text = `Check out my resume: ${fileName}`;
                            const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
                            window.open(whatsappUrl, '_blank');
                            toast.info('Opening WhatsApp... Please attach the downloaded PDF file.');
                          }}
                          sx={{
                            borderColor: '#25D366',
                            color: '#25D366',
                            '&:hover': { borderColor: '#25D366', bgcolor: 'rgba(37, 211, 102, 0.1)' }
                          }}
                        >
                          WhatsApp
                        </Button>
                      </Grid>
                      <Grid item xs={4}>
                        <Button
                          variant="outlined"
                          fullWidth
                          size="small"
                          startIcon={<TelegramIcon />}
                          onClick={() => {
                            const fullName = (profile.fullName || 'Resume').trim();
                            const firstName = fullName.split(/\s+/)[0].toLowerCase().replace(/[^a-zA-Z0-9]/g, '');
                            const fileName = `${firstName}_resume.pdf`;
                            // Use current window location to ensure correct port
                            const baseUrl = window.location.origin;
                            const resumeUrl = `${baseUrl}/user/resume`;
                            const text = `Check out my resume: ${fileName}\n${resumeUrl}`;
                            const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(resumeUrl)}&text=${encodeURIComponent(text)}`;
                            window.open(telegramUrl, '_blank');
                            toast.info('Opening Telegram... Please attach the downloaded PDF file.');
                          }}
                          sx={{
                            borderColor: '#0088cc',
                            color: '#0088cc',
                            '&:hover': { borderColor: '#0088cc', bgcolor: 'rgba(0, 136, 204, 0.1)' }
                          }}
                        >
                          Telegram
                        </Button>
                      </Grid>
                      <Grid item xs={4}>
              <Button
                          variant="outlined"
                          fullWidth
                          size="small"
                          startIcon={<EmailIcon />}
                          onClick={() => {
                            const fullName = (profile.fullName || 'Resume').trim();
                            const firstName = fullName.split(/\s+/)[0].toLowerCase().replace(/[^a-zA-Z0-9]/g, '');
                            const fileName = `${firstName}_resume.pdf`;
                            const subject = encodeURIComponent(`Resume - ${profile.fullName || 'Resume'}`);
                            const body = encodeURIComponent(`Please find my resume attached.\n\nBest regards,\n${profile.fullName || ''}`);
                            const mailtoUrl = `mailto:?subject=${subject}&body=${body}`;
                            window.location.href = mailtoUrl;
                            toast.info('Opening email client... Please attach the downloaded PDF file.');
                          }}
                          sx={{
                            borderColor: '#EA4335',
                            color: '#EA4335',
                            '&:hover': { borderColor: '#EA4335', bgcolor: 'rgba(234, 67, 53, 0.1)' }
                          }}
                        >
                          Email
              </Button>
                      </Grid>
                    </Grid>
              </Box>
            )}
          </CardContent>
        </Card>

      {/* Profile Preview */}
            <Card sx={{ mt: 2 }}>
          <CardContent>
                <Typography variant="h6" gutterBottom>
              Profile Preview
            </Typography>
                <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
                  <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Name</Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {profile.fullName || 'N/A'}
                    </Typography>
              </Grid>
                  <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Email</Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {profile.email || 'N/A'}
                    </Typography>
              </Grid>
                  <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Phone</Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {profile.phone || 'N/A'}
                    </Typography>
              </Grid>
                  <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Qualification</Typography>
                <Typography variant="body1" fontWeight={600}>
                  {profile.qualification ? profile.qualification.charAt(0).toUpperCase() + profile.qualification.slice(1) : 'N/A'}
                </Typography>
              </Grid>
              {profile.skills && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Skills</Typography>
                      <Typography variant="body1">
                    {Array.isArray(profile.skills) ? profile.skills.join(', ') : profile.skills}
                  </Typography>
                </Grid>
              )}
            </Grid>
              <Button
                variant="outlined"
                  startIcon={<EditIcon />}
                onClick={() => navigate('/user/career-profile')}
                  fullWidth
                  sx={{ mt: 2 }}
              >
                Edit Profile
              </Button>
              {editFeeRupees > 0 && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Later PDF after edits may include ₹{editFeeRupees} edit fee
                </Typography>
              )}
          </CardContent>
        </Card>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default ResumeGeneration;
